import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  PlusIcon, 
  UserIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.mythicalarc.com/api'
  : 'http://localhost:8000/api';

const EnhancedAdminPanel = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [systemStats, setSystemStats] = useState({});
  const [activeTab, setActiveTab] = useState('users');
  
  // Create user form state
  const [createUserForm, setCreateUserForm] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'user',
    can_chat: true,
    can_upload: false,
    can_use_voice: false,
    can_use_agents: false
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'system') {
      fetchSystemStats();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemStats = async () => {
    try {
      // Mock system stats - in real implementation, this would come from API
      setSystemStats({
        totalUsers: users.length,
        activeUsers: users.filter(u => u.is_active).length,
        totalSessions: 1247,
        systemUptime: '15 days 6 hours',
        memoryUsage: '2.4 GB',
        diskUsage: '45.2 GB',
        apiCalls: 89247,
        errorRate: '0.2%'
      });
    } catch (error) {
      console.error('Failed to fetch system stats:', error);
    }
  };

  const togglePermission = async (userId, permission) => {
    try {
      const userToUpdate = users.find(u => u.id === userId);
      const updatedUser = {
        ...userToUpdate,
        [permission]: !userToUpdate[permission]
      };

      const response = await axios.put(`${API_BASE_URL}/admin/users/${userId}`, updatedUser);
      
      setUsers(users.map(u => u.id === userId ? response.data : u));
    } catch (error) {
      console.error('Failed to update permission:', error);
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      const userToUpdate = users.find(u => u.id === userId);
      const updatedUser = {
        ...userToUpdate,
        is_active: !userToUpdate.is_active
      };

      const response = await axios.put(`${API_BASE_URL}/admin/users/${userId}`, updatedUser);
      
      setUsers(users.map(u => u.id === userId ? response.data : u));
    } catch (error) {
      console.error('Failed to update user status:', error);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/admin/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/users`, createUserForm);
      setUsers([...users, response.data]);
      setShowCreateModal(false);
      setCreateUserForm({
        username: '',
        email: '',
        full_name: '',
        password: '',
        role: 'user',
        can_chat: true,
        can_upload: false,
        can_use_voice: false,
        can_use_agents: false
      });
    } catch (error) {
      setCreateError(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const resetCreateForm = () => {
    setCreateUserForm({
      username: '',
      email: '',
      full_name: '',
      password: '',
      role: 'user',
      can_chat: true,
      can_upload: false,
      can_use_voice: false,
      can_use_agents: false
    });
    setCreateError('');
  };

  const PermissionToggle = ({ userId, permission, enabled, label }) => (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-gray-700">{label}</span>
      <button
        onClick={() => togglePermission(userId, permission)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          enabled ? 'bg-blue-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  const CreateUserModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Create New User</h3>
            <button
              onClick={() => {
                setShowCreateModal(false);
                resetCreateForm();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {createError && (
            <div className="mb-4 bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded">
              {createError}
            </div>
          )}

          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username *
              </label>
              <input
                type="text"
                required
                value={createUserForm.username}
                onChange={(e) => setCreateUserForm({...createUserForm, username: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                required
                value={createUserForm.email}
                onChange={(e) => setCreateUserForm({...createUserForm, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={createUserForm.full_name}
                onChange={(e) => setCreateUserForm({...createUserForm, full_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password *
              </label>
              <input
                type="password"
                required
                value={createUserForm.password}
                onChange={(e) => setCreateUserForm({...createUserForm, password: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={createUserForm.role}
                onChange={(e) => setCreateUserForm({...createUserForm, role: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="guest">Guest</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Permissions
              </label>
              <div className="space-y-2">
                {[
                  { key: 'can_chat', label: 'Chat Access' },
                  { key: 'can_upload', label: 'File Upload' },
                  { key: 'can_use_voice', label: 'Voice Features' },
                  { key: 'can_use_agents', label: 'AI Agents' }
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{label}</span>
                    <button
                      type="button"
                      onClick={() => setCreateUserForm({
                        ...createUserForm,
                        [key]: !createUserForm[key]
                      })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        createUserForm[key] ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          createUserForm[key] ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const UserCard = ({ userItem }) => (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      {/* User Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
            <UserIcon className="h-8 w-8 text-gray-600" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-medium text-gray-900">
              {userItem.full_name || userItem.username}
            </h3>
            <p className="text-sm text-gray-600">{userItem.email}</p>
            <div className="flex items-center mt-1">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                userItem.role === 'admin' 
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-green-100 text-green-800'
              }`}>
                {userItem.role}
              </span>
              <button
                onClick={() => toggleUserStatus(userItem.id)}
                className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  userItem.is_active 
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-red-100 text-red-800 hover:bg-red-200'
                }`}
              >
                {userItem.is_active ? 'Active' : 'Inactive'}
              </button>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setEditingUser(userItem)}
            className="p-2 text-gray-400 hover:text-blue-600"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          {userItem.id !== user.id && (
            <button
              onClick={() => deleteUser(userItem.id)}
              className="p-2 text-gray-400 hover:text-red-600"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Permissions */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Permissions</h4>
        <div className="space-y-2">
          <PermissionToggle
            userId={userItem.id}
            permission="can_chat"
            enabled={userItem.can_chat}
            label="Chat Access"
          />
          <PermissionToggle
            userId={userItem.id}
            permission="can_upload"
            enabled={userItem.can_upload}
            label="File Upload"
          />
          <PermissionToggle
            userId={userItem.id}
            permission="can_use_voice"
            enabled={userItem.can_use_voice}
            label="Voice Features"
          />
          <PermissionToggle
            userId={userItem.id}
            permission="can_use_agents"
            enabled={userItem.can_use_agents}
            label="AI Agents"
          />
        </div>
      </div>

      {/* Usage Stats */}
      <div className="border-t border-gray-200 pt-4 mt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Usage Stats</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Created:</span>
            <div className="font-medium">
              {new Date(userItem.created_at).toLocaleDateString()}
            </div>
          </div>
          <div>
            <span className="text-gray-600">Last Active:</span>
            <div className="font-medium">
              {userItem.updated_at 
                ? new Date(userItem.updated_at).toLocaleDateString()
                : 'Never'
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const SystemOverview = () => (
    <div className="space-y-6">
      {/* System Health */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">System Health</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {systemStats.systemUptime}
            </div>
            <div className="text-sm text-gray-600">Uptime</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {systemStats.memoryUsage}
            </div>
            <div className="text-sm text-gray-600">Memory Usage</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {systemStats.apiCalls}
            </div>
            <div className="text-sm text-gray-600">API Calls</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {systemStats.errorRate}
            </div>
            <div className="text-sm text-gray-600">Error Rate</div>
          </div>
        </div>
      </div>

      {/* User Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">User Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-600 mb-2">User Distribution</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Total Users:</span>
                <span className="font-medium">{systemStats.totalUsers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Active Users:</span>
                <span className="font-medium text-green-600">{systemStats.activeUsers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Inactive Users:</span>
                <span className="font-medium text-red-600">
                  {systemStats.totalUsers - systemStats.activeUsers}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-600 mb-2">Permissions Overview</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Chat Enabled:</span>
                <span className="font-medium">
                  {users.filter(u => u.can_chat).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Upload Enabled:</span>
                <span className="font-medium">
                  {users.filter(u => u.can_upload).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Agents Enabled:</span>
                <span className="font-medium">
                  {users.filter(u => u.can_use_agents).length}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-600 mb-2">Role Distribution</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Admins:</span>
                <span className="font-medium text-purple-600">
                  {users.filter(u => u.role === 'admin').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Users:</span>
                <span className="font-medium text-blue-600">
                  {users.filter(u => u.role === 'user').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Guests:</span>
                <span className="font-medium text-gray-600">
                  {users.filter(u => u.role === 'guest').length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Enhanced Admin Panel</h1>
          <p className="text-gray-600">Manage users, permissions, and system settings</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 md:mt-0">
          <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white px-3 py-1 rounded-lg text-sm font-medium">
            Phase 5
          </div>
          {activeTab === 'users' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary inline-flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create User
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'users', name: 'User Management', icon: UserIcon },
            { id: 'system', name: 'System Overview', icon: ChartBarIcon },
            { id: 'settings', name: 'Settings', icon: Cog6ToothIcon }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className={`mr-2 h-5 w-5 ${
                activeTab === tab.id ? 'text-red-600' : 'text-gray-400 group-hover:text-gray-500'
              }`} />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'users' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {users.map((userItem) => (
            <UserCard key={userItem.id} userItem={userItem} />
          ))}
        </div>
      )}

      {activeTab === 'system' && <SystemOverview />}

      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">System Settings</h3>
          <p className="text-gray-600">System configuration options will be available here.</p>
        </div>
      )}

      {/* No Users State */}
      {activeTab === 'users' && users.length === 0 && (
        <div className="text-center py-12">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new user.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary inline-flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create User
            </button>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && <CreateUserModal />}
    </div>
  );
};

export default EnhancedAdminPanel;